import type { DongTyLe, HinhThucChia, VaiTroChia } from '@qlk/shared';
import {
  HINH_THUC_CHIA,
  HINH_THUC_CHIA_LABEL,
  NGUONG_CANH_BAO_PCT,
  NGUONG_CHAN_PCT,
  VAI_TRO_CHIA_LABEL,
} from '@qlk/shared';
import { useState } from 'react';

import { HopThoai, Nut, O, OChonTruong, homNayVn } from '../../../ui/index.js';
import { useLuuTyLeKenh, useLuuTyLeTruongNhom, useThanhVien } from '../api.js';

export interface HopTyLeProps {
  /** Đặt tỷ lệ cho kênh này (quản lý / editor). */
  channelId?: string;
  /** Đặt tỷ lệ trưởng nhóm cho nhóm này. */
  teamId?: string;
  /** Có sẵn = "Đổi tỷ lệ từ ngày…" cho đúng người + vai trò đó. */
  dangDoi?: DongTyLe;
  /** Tổng % hiện tại của kênh, để cảnh báo ngay khi đang gõ. */
  tongPctHienTai?: number;
  onDong: () => void;
  onXong?: (canhBao: string | null, daGanQuyenKenh: boolean) => void;
}

/**
 * Thêm hoặc **đổi** một dòng tỷ lệ.
 *
 * Luôn là *thêm dòng mới*: máy chủ đóng dòng cũ ở ngày liền trước `hieuLucTu`
 * chứ không ghi đè, nên hộp này không có chế độ "sửa". Vì vậy ô ngày hiệu lực
 * là bắt buộc và mặc định là hôm nay.
 */
export function HopTyLe({
  channelId,
  teamId,
  dangDoi,
  tongPctHienTai = 0,
  onDong,
  onXong,
}: HopTyLeProps) {
  const thanhVien = useThanhVien();
  const luuKenh = useLuuTyLeKenh();
  const luuNhom = useLuuTyLeTruongNhom();
  const luu = teamId ? luuNhom : luuKenh;

  const [userId, setUserId] = useState(dangDoi?.userId ?? '');
  const [vaiTro, setVaiTro] = useState<VaiTroChia>(
    dangDoi?.vaiTro ?? (teamId ? 'truong_nhom' : 'quan_ly'),
  );
  const [hinhThuc, setHinhThuc] = useState<HinhThucChia>(dangDoi?.hinhThuc ?? 'pct');
  const [pct, setPct] = useState(dangDoi ? String(dangDoi.pct) : '');
  const [coDinh, setCoDinh] = useState(dangDoi ? String(dangDoi.coDinhUsd) : '');
  const [hieuLucTu, setHieuLucTu] = useState(homNayVn());
  const [ghiChu, setGhiChu] = useState('');

  /* Người đã lưu trữ (nghỉ việc) không chọn mới được — máy chủ cũng chặn. */
  const nguoi = (thanhVien.data ?? [])
    .filter((n) => n.status !== 'archived')
    .map((n) => ({ value: n.id, label: `${n.name} · ${n.email}` }));

  const soPct = Number(pct.replace(',', '.'));
  const soCoDinh = Number(coDinh.replace(',', '.'));
  const canPct = hinhThuc !== 'co_dinh';
  const canCoDinh = hinhThuc !== 'pct';

  const hopLe =
    userId !== '' &&
    hieuLucTu !== '' &&
    (!canPct || (Number.isFinite(soPct) && soPct > 0 && soPct <= 100)) &&
    (!canCoDinh || (Number.isFinite(soCoDinh) && soCoDinh > 0));

  /*
   * Tổng % sau khi lưu: dòng cũ của **cùng người + cùng vai trò** sẽ bị đóng,
   * nên khi đang đổi thì trừ phần cũ ra trước khi cộng phần mới.
   */
  const tongSauKhiLuu =
    tongPctHienTai -
    (dangDoi?.hinhThuc === 'co_dinh' ? 0 : (dangDoi?.pct ?? 0)) +
    (canPct && Number.isFinite(soPct) ? soPct : 0);
  const vuotChan = tongSauKhiLuu > NGUONG_CHAN_PCT;
  const vuotCanhBao = !vuotChan && tongSauKhiLuu > NGUONG_CANH_BAO_PCT;

  return (
    <HopThoai
      title={dangDoi ? 'Đổi tỷ lệ từ ngày…' : 'Thêm người vào đội ngũ'}
      rong="md"
      onDong={onDong}
      chanHop={
        <>
          {luu.isError && (
            <p role="alert" className="mr-auto self-center text-phu text-bad-chu">
              {luu.error.message}
            </p>
          )}
          <Nut kieu="phu" onClick={onDong}>
            Huỷ
          </Nut>
          <Nut
            kieu="chinh"
            disabled={!hopLe || vuotChan}
            dangChay={luu.isPending}
            onClick={() => {
              const than = {
                userId,
                vaiTro,
                hinhThuc,
                ...(canPct ? { pct: soPct } : {}),
                ...(canCoDinh ? { coDinhUsd: soCoDinh } : {}),
                hieuLucTu,
                ...(ghiChu.trim() ? { ghiChu: ghiChu.trim() } : {}),
              };

              if (teamId) {
                luuNhom.mutate(
                  { teamId, ...than, vaiTro: 'truong_nhom' },
                  {
                    onSuccess: (kq) => {
                      onXong?.(kq.canhBao, kq.daGanQuyenKenh);
                      onDong();
                    },
                  },
                );
                return;
              }
              if (!channelId) return;
              luuKenh.mutate(
                { channelId, ...than },
                {
                  onSuccess: (kq) => {
                    onXong?.(kq.canhBao, kq.daGanQuyenKenh);
                    onDong();
                  },
                },
              );
            }}
          >
            Lưu dòng mới
          </Nut>
        </>
      }
    >
      <p className="text-phu text-chu-mo">
        Lưu là <strong>thêm dòng mới</strong>: dòng cũ của người này được đóng ở ngày liền trước{' '}
        ngày hiệu lực, không bị ghi đè. Kỳ đang chạy sẽ chia theo số ngày của từng dòng.
      </p>

      <div className="mt-4 space-y-4">
        <OChonTruong
          label="Người"
          value={userId}
          onChange={setUserId}
          options={[{ value: '', label: '— chọn người —' }, ...nguoi]}
          moTa={dangDoi ? 'Đang đổi tỷ lệ của người này' : 'Nhân sự đã nghỉ không hiện ở đây'}
        />

        {!teamId && (
          <OChonTruong
            label="Vai trò"
            value={vaiTro}
            onChange={(v) => {
              setVaiTro(v as VaiTroChia);
            }}
            options={[
              { value: 'quan_ly', label: VAI_TRO_CHIA_LABEL.quan_ly },
              { value: 'editor', label: VAI_TRO_CHIA_LABEL.editor },
            ]}
            moTa="Trưởng nhóm đặt ở trang Nhóm & đội ngũ, không đặt ở kênh"
          />
        )}

        <OChonTruong
          label="Hình thức"
          value={hinhThuc}
          onChange={(v) => {
            setHinhThuc(v as HinhThucChia);
          }}
          options={HINH_THUC_CHIA.map((h) => ({ value: h, label: HINH_THUC_CHIA_LABEL[h] }))}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {canPct && (
            <O
              label="Phần trăm (%)"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={pct}
              onChange={setPct}
              moTa="Tính trên lợi nhuận kênh của kỳ"
            />
          )}
          {canCoDinh && (
            <O
              label="Cố định (US$/tháng)"
              type="number"
              min={0}
              step={1}
              value={coDinh}
              onChange={setCoDinh}
              moTa="Chia theo ngày nếu không phủ hết kỳ"
            />
          )}
        </div>

        <O
          label="Hiệu lực từ ngày"
          type="date"
          value={hieuLucTu}
          onChange={setHieuLucTu}
          required
          moTa="Kỳ cũ giữ nguyên số đã chia"
        />

        <O label="Ghi chú" value={ghiChu} onChange={setGhiChu} nhieuDong />

        {(vuotChan || vuotCanhBao) && (
          <p
            role="alert"
            className={`rounded-[10px] px-3 py-2 text-phu ${
              vuotChan ? 'bg-bad-nen text-bad-chu' : 'bg-warn-nen text-warn-chu'
            }`}
          >
            Tổng tỷ lệ của kênh sau khi lưu là{' '}
            {tongSauKhiLuu.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%
            {vuotChan
              ? ` — vượt ${String(NGUONG_CHAN_PCT)}%, không lưu được.`
              : ` — vượt ${String(NGUONG_CANH_BAO_PCT)}%, vẫn lưu được.`}
          </p>
        )}
      </div>
    </HopThoai>
  );
}
